import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { API_URL, formatDate } from '../lib/utils';
import { Loader2, Users, MailCheck, MailX, Search, Download, X, Briefcase, CalendarDays } from 'lucide-react';

const TYPE_TABS = [
  ['all', 'All Sign-Ups'], ['job_seeker', 'Job Seekers'], ['employer', 'Employers'],
  ['supervisor', 'LCSW-S Supervisors'], ['private_practice', 'Private Practice'],
  ['group_practice', 'Group Practice'], ['internship_user', 'Internship Users'], ['other', 'Other'],
];
const TYPE_LABEL = Object.fromEntries(TYPE_TABS);
const FREQ_LABEL = { immediate: 'Immediate', daily: 'Daily', weekly: 'Weekly', off: 'Off' };
const DATE_TABS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['7', 'Last 7 Days'], ['30', 'Last 30 Days'], ['all', 'All Time']];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const inRange = (createdAt, range) => {
  if (range === 'all' || !createdAt) return range === 'all';
  const c = new Date(createdAt); const now = new Date();
  const today = startOfDay(now);
  if (range === 'today') return c >= today;
  if (range === 'yesterday') { const y = new Date(today); y.setDate(y.getDate() - 1); return c >= y && c < today; }
  const days = parseInt(range, 10); const from = new Date(today); from.setDate(from.getDate() - (days - 1));
  return c >= from;
};

const EmployerModal = ({ userId, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/admin/employers/${userId}/jobs`).then((r) => setData(r.data)).catch(() => setData({ error: true })); }, [userId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="employer-jobs-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 relative max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        {!data ? <div className="py-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : data.error ? <p className="text-red-600">Could not load.</p> : (
          <>
            <h3 className="font-serif text-xl font-bold text-primary">{data.employer.company_name || data.employer.name}</h3>
            <p className="text-sm text-gray-500">{data.employer.name} · {data.employer.email} · Joined {formatDate(data.employer.created_at)}</p>
            <p className="text-sm mt-1"><span className="font-semibold">{data.total_jobs}</span> jobs posted · <span className="font-semibold text-green-700">{data.live_jobs}</span> live</p>
            <div className="mt-4 border rounded-lg overflow-hidden">
              <table className="data-table"><thead><tr><th>Job Title</th><th>Live?</th><th>Source</th><th>Posted</th></tr></thead>
                <tbody>
                  {data.jobs.map((j) => (
                    <tr key={j.job_id}><td className="font-medium">{j.title}</td>
                      <td>{j.is_live ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Live</Badge> : <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">{j.status}</Badge>}</td>
                      <td>{j.source}</td><td>{formatDate(j.created_at)}</td></tr>
                  ))}
                  {data.jobs.length === 0 && <tr><td colSpan={4} className="text-center text-gray-400 py-6">No jobs posted.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const AdminTabUsers = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, receiving_emails: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeTab, setTypeTab] = useState('all');
  const [dateTab, setDateTab] = useState('all');
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;
  const [empModal, setEmpModal] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/admin/signups`).then((res) => {
      setRows(res.data.users || []); setCounts(res.data.counts || {});
    }).catch((e) => console.error(e)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => rows.filter((u) => {
    if (typeTab !== 'all' && u.registration_type !== typeTab) return false;
    if (!inRange(u.created_at, dateTab)) return false;
    if (q && !(`${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [rows, q, typeTab, dateTab]);

  useEffect(() => { setPage(1); }, [q, typeTab, dateTab]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageSafe = Math.min(page, totalPages);
  const paged = filtered.slice((pageSafe - 1) * PER_PAGE, pageSafe * PER_PAGE);

  const todaySummary = useMemo(() => {
    const today = rows.filter((u) => inRange(u.created_at, 'today'));
    const groups = {};
    today.forEach((u) => { (groups[u.registration_type] = groups[u.registration_type] || []).push(u); });
    return { count: today.length, groups };
  }, [rows]);

  const typeCount = (t) => rows.filter((u) => (t === 'all' || u.registration_type === t) && inRange(u.created_at, dateTab)).length;

  const exportCsv = () => {
    const header = ['Full Name', 'Email', 'Date Signed Up', 'Registration Type', 'Test Account', 'Email Notifications', 'Job Alert Frequency', 'Unsubscribed'];
    const lines = filtered.map((u) => [u.name, u.email, formatDate(u.created_at), u.roles_label || TYPE_LABEL[u.registration_type] || u.registration_type,
      u.is_test ? 'TEST' : 'No', u.email_notifications ? 'Yes' : 'No', FREQ_LABEL[u.job_alert_frequency], u.unsubscribed ? 'Yes' : 'No']
      .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'inasw-signups.csv'; a.click();
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading sign-ups…</div>;

  return (
    <div data-testid="admin-users-signups">
      {/* Today's New Sign-Ups */}
      <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 mb-5" data-testid="todays-signups">
        <div className="flex items-center gap-2 mb-2"><CalendarDays className="w-4 h-4 text-primary" /><h3 className="font-semibold text-primary">Today's New Sign-Ups — {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</h3></div>
        {todaySummary.count === 0 ? <p className="text-sm text-gray-500">No new sign-ups yet today.</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(todaySummary.groups).map(([t, list]) => (
              <div key={t} className="bg-white rounded-lg border border-gray-100 p-3">
                <p className="text-sm font-bold text-primary">{TYPE_LABEL[t] || t} — {list.length}</p>
                <ul className="text-xs text-gray-600 mt-1 space-y-0.5">{list.slice(0, 8).map((u) => <li key={u.user_id}>{u.name}</li>)}</ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-5" data-testid="signup-totals">
        {[
          ['Total', counts.total, 'text-primary'],
          ['Job Seekers', counts.job_seekers, 'text-primary'],
          ['Employers', counts.employers, 'text-primary'],
          ['Private Practice', counts.private_practice, 'text-primary'],
          ['Interns / Students', counts.interns, 'text-primary'],
          ['Supervisors', counts.supervisors, 'text-primary'],
          ['Other', counts.other, 'text-primary'],
        ].map(([label, val, cls]) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <div className={`text-2xl font-bold ${cls}`}>{val ?? 0}</div>
            <div className="text-[11px] text-gray-500 leading-tight mt-0.5">{label}</div>
          </div>
        ))}
      </div>
      {counts.test_accounts > 0 && (
        <p className="text-xs text-gray-400 mb-4" data-testid="test-accounts-note">Totals exclude {counts.test_accounts} test account(s), which are labeled “TEST — DO NOT CONTACT” below.</p>
      )}

      {/* Date filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        {DATE_TABS.map(([k, label]) => (
          <button key={k} onClick={() => setDateTab(k)} className={`text-sm px-3 py-1.5 rounded-full border ${dateTab === k ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200'}`} data-testid={`date-filter-${k}`}>{label}</button>
        ))}
      </div>

      {/* Type tabs */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-100 pb-3">
        {TYPE_TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTypeTab(k)} className={`text-sm px-3 py-1.5 rounded-lg ${typeTab === k ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`type-tab-${k}`}>
            {label} <span className="opacity-70">({typeCount(k)})</span>
          </button>
        ))}
      </div>

      {/* Search + export */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9" data-testid="users-search" />
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="users-export-btn"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
      </div>

      <div className="text-xs text-gray-500 mb-2" data-testid="users-result-count">Showing {paged.length} of {filtered.length} users (page {pageSafe} of {totalPages})</div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Full Name</th><th>Email</th><th>Date Signed Up</th><th>Registration Type</th>
                <th>Email Notifications</th><th>Job Alert Frequency</th><th>Unsubscribed</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((u) => (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <td className="font-medium">
                    {u.registration_type === 'employer'
                      ? <button onClick={() => setEmpModal(u.user_id)} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`employer-drilldown-${u.user_id}`}>{u.name} <Briefcase className="w-3 h-3" /></button>
                      : u.name}
                    {u.is_test && <span className="ml-2 text-[10px] font-semibold text-white bg-gray-500 rounded px-1.5 py-0.5" data-testid={`test-badge-${u.user_id}`}>TEST — DO NOT CONTACT</span>}
                  </td>
                  <td>{u.email}</td>
                  <td>{formatDate(u.created_at)}</td>
                  <td><Badge variant="outline">{u.roles_label || TYPE_LABEL[u.registration_type] || u.registration_type}</Badge></td>
                  <td>{u.email_notifications ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Yes</Badge> : <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">No</Badge>}</td>
                  <td>{FREQ_LABEL[u.job_alert_frequency] || u.job_alert_frequency}</td>
                  <td>{u.unsubscribed ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Yes</Badge> : <span className="text-gray-400">No</span>}</td>
                </tr>
              ))}
              {paged.length === 0 && <tr><td colSpan={7} className="text-center text-gray-400 py-10">No users match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-4" data-testid="signups-pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe === 1} className="px-3 py-1 rounded border text-sm disabled:opacity-40" data-testid="page-prev">Previous</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((n) => Math.abs(n - pageSafe) <= 2 || n === 1 || n === totalPages)
            .map((n, idx, arr) => (
              <React.Fragment key={n}>
                {idx > 0 && n - arr[idx - 1] > 1 && <span className="px-1 text-gray-400">…</span>}
                <button onClick={() => setPage(n)} className={`px-3 py-1 rounded border text-sm ${n === pageSafe ? 'bg-primary text-white border-primary' : ''}`} data-testid={`page-${n}`}>{n}</button>
              </React.Fragment>
            ))}
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe === totalPages} className="px-3 py-1 rounded border text-sm disabled:opacity-40" data-testid="page-next">Next</button>
        </div>
      )}

      {empModal && <EmployerModal userId={empModal} onClose={() => setEmpModal(null)} />}
    </div>
  );
};

export default AdminTabUsers;
